import React from "react";
import { useSelector } from "react-redux";
import clsx from "clsx";
import moment from "moment";
import { Avatar, Box, Typography } from "@material-ui/core";
import {
  CustomCard,
  CustomCopyToClipboard,
  SocialLinks,
  CustomBadge,
  LeadTypeBadges,
} from "../../../../../Components";
import { getDownloadableLink } from "../../../../../Helper";
import { useSelectedTheme, useTranslate } from "../../../../../Hooks";

import useStyles from "./styles";
import { FemaleIcon, MaleIcon } from "../../../../../assets/icons";

function ClientProfileCard({ contact = {} }) {
  const styles = useStyles();
  const details = contact;
  const {
    theme: { palette },
  } = useSelectedTheme();
  const { translate } = useTranslate("AfterSalesClientProfileView");
  const { isDarkMode } = useSelector((state) => state.theme);

  return (
    <Box className={styles.container}>
      <CustomCard
        classes={clsx(styles.card, {
          [styles.corprateCard]: details?.contact_type_id === 2,
        })}
      >
        <Box className={styles.contactDetailsContainer}>
          <Avatar
            className={
              details?.imagePath ? styles.avatar : styles.defaultAvatar
            }
            src={
              details?.imagePath
                ? getDownloadableLink(details?.imagePath)
                : details?.contact_type_id === 2 // "CORPORATE"
                ? "/images/contacts/CorporateAvatar.svg"
                : details?.gender?.lookupItemName === "Male"
                ? "/images/contacts/MaleAvatar.svg"
                : "/images/contacts/FemaleAvatar.svg"
            }
          />
          <Box className={styles.contactDetails}>
            <Typography className={styles.contactName}>
              {details?.first_name} {details?.last_name}
              {details?.company_name || ""}
            </Typography>
            <Box display={"flex"} gridGap={"8px"}>
              <Typography
                className={styles.contactType}
                variant="body2"
                component="p"
              >
                {details.contact_type_id === 2
                  ? translate("corporate")
                  : translate("individual")}
              </Typography>
              {details?.contact_class?.lookupItemName === "VIP" && (
                <CustomBadge
                  Style={{
                    padding: "0 2px",
                    borderRadius: "2px",
                    lineHeight: "20px",
                  }}
                  label={translate("VIP")}
                  BackgroundColor={palette.utility.brand_300}
                  BorderColor={palette.utility.brand_300}
                  Color={"#FFF"}
                />
              )}

              {details?.contact_class?.lookupItemName === "VVIP" && (
                <CustomBadge
                  Style={{
                    padding: "0 2px",
                    borderRadius: "2px",
                    lineHeight: "20px",
                  }}
                  label={translate("VVIP")}
                  BackgroundColor={palette.utility.brand_600}
                  BorderColor={palette.utility.brand_600}
                  Color={"#FFF"}
                />
              )}
              <CustomCopyToClipboard
                data={details.Ids} // copy
                childrenData={`#${details.Ids}`} // render
                copyDoneShowTime={1000}
              />
              {details.contact_type_id === 1 &&
                (details?.gender?.lookupItemName?.toLowerCase() === "male" ? (
                  <MaleIcon
                    fill={isDarkMode ? "#CECFD2" : "#344054"}
                    width={24}
                    height={24}
                  />
                ) : (
                  <FemaleIcon
                    fill={isDarkMode ? "#CECFD2" : "#344054"}
                    width={24}
                    height={24}
                  />
                ))}
            </Box>
            <SocialLinks
              contactId={details?.Ids}
              isAfterSale
              activeItem={details}
              gap={8}
            />
            <Typography variant="body2" component="p">
              <span className={styles.creationLabel}>
                {translate("creation-on")}
              </span>
              <span className={styles.creationTime}>
                {moment(details.createdOn).format("DD/MM/YYYY - hh:mm A")}
              </span>{" "}
              <span className={styles.contactCreator}>{details.createdBy}</span>
            </Typography>
          </Box>
        </Box>

        <Box style={{ marginTop: 16 }}>
          {details?.leadTypes && <LeadTypeBadges leadTypesList={details.leadTypes} />}
        </Box>
      </CustomCard>
      {details.contact_type_id === 1 && (
        <Box className={styles.summaryContainer}>
          <Typography className={styles.summaryTitle} variant="h6">
            {translate("client-summary")}
          </Typography>
          <Box className={styles.summaryDetails}>
            <Box className={styles.summaryField}>
              <Typography variant="body2" component="p">
                {translate("date-of-birth")}
              </Typography>
              <Typography variant="body2" component="p">
                {details?.date_of_birth
                  ? moment(details?.date_of_birth).format("DD-MM-YYYY")
                  : "N/A"}
              </Typography>
            </Box>
            <Box className={styles.summaryField}>
              <Typography variant="body2" component="p">
                {translate("nationality")}
              </Typography>
              <Typography variant="body2" component="p">
                {details?.allDetails["Main Information"]?.find(
                  (info) => info.title === "nationality"
                )?.value || "N/A"}
              </Typography>
            </Box>
            <Box className={styles.summaryField}>
              <Typography variant="body2" component="p">
                {translate("residence")}
              </Typography>
              <Typography variant="body2" component="p">
                {details?.country?.lookupItemName || "N/A"}
              </Typography>
            </Box>
            <Box className={styles.summaryField}>
              <Typography variant="body2" component="p">
                {translate("languages")}
              </Typography>
              <Typography variant="body2" component="p">
                {details?.language?.lookupItemName || "N/A"}
              </Typography>
            </Box>
            <Box className={styles.summaryField}>
              <Typography variant="body2" component="p">
                {translate("email")}
              </Typography>
              <Typography variant="body2" component="p">
                {details?.email_address?.email ||
                  details?.general_email?.email ||
                  "N/A"}
              </Typography>
            </Box>
            <Box className={styles.summaryField}>
              <Typography variant="body2" component="p">
                {translate("phone-number")}
              </Typography>
              <Typography variant="body2" component="p">
                {details?.mobile?.phone ||
                  details?.landline_number?.phone ||
                  "N/A"}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default ClientProfileCard;
